import { useState, useEffect } from "react";
import { apiClient } from "../../App";
import CollapsibleCard from "../../components/CollapsibleCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Settings as SettingsIcon } from "lucide-react";

/**
 * Invoicing settings card — invoice prefix, bank details, cheque details.
 * Self-contained: fetches /band/settings on mount, owns all form state.
 *
 * Extracted from Settings.jsx in iter-66d.
 */
const InvoicingSettingsCard = () => {
  const [invoicePrefix, setInvoicePrefix] = useState("");
  const [savingInvoicePrefix, setSavingInvoicePrefix] = useState(false);
  const [bank, setBank] = useState({ bank_account_name: "", bank_sort_code: "", bank_account_number: "" });
  const [savingBank, setSavingBank] = useState(false);
  const [cheque, setCheque] = useState({ cheque_payable_to: "", cheque_postal_address: "" });
  const [savingCheque, setSavingCheque] = useState(false);

  const fetchBandSettings = async () => {
    try {
      const res = await apiClient.get("/band/settings");
      setInvoicePrefix(res.data?.invoice_prefix || "");
      setBank({
        bank_account_name: res.data?.bank_account_name || "",
        bank_sort_code: res.data?.bank_sort_code || "",
        bank_account_number: res.data?.bank_account_number || "",
      });
      setCheque({
        cheque_payable_to: res.data?.cheque_payable_to || "",
        cheque_postal_address: res.data?.cheque_postal_address || "",
      });
    } catch {
      // silent
    }
  };

  useEffect(() => {
    fetchBandSettings();
  }, []);

  const formatSortCode = (v) => {
    const digits = (v || "").replace(/\D/g, "").slice(0, 6);
    const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)].filter(Boolean);
    return parts.join("-");
  };

  const saveInvoicePrefix = async () => {
    setSavingInvoicePrefix(true);
    try {
      await apiClient.put("/band/settings", { invoice_prefix: invoicePrefix });
      toast.success("Invoice prefix saved");
      await fetchBandSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save invoice prefix");
    } finally {
      setSavingInvoicePrefix(false);
    }
  };

  const saveBankDetails = async () => {
    setSavingBank(true);
    try {
      await apiClient.put("/band/settings", {
        bank_account_name: bank.bank_account_name,
        bank_sort_code: bank.bank_sort_code,
        bank_account_number: bank.bank_account_number,
      });
      toast.success("Bank details saved");
      await fetchBandSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save bank details");
    } finally {
      setSavingBank(false);
    }
  };

  const saveChequeDetails = async () => {
    setSavingCheque(true);
    try {
      await apiClient.put("/band/settings", {
        cheque_payable_to: cheque.cheque_payable_to,
        cheque_postal_address: cheque.cheque_postal_address,
      });
      toast.success("Cheque details saved");
      await fetchBandSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save cheque details");
    } finally {
      setSavingCheque(false);
    }
  };

  return (
    <CollapsibleCard icon={SettingsIcon} iconColor="text-emerald-400" title="Invoicing" className="border-emerald-500/20" testId="invoicing-settings-card">
      <div className="space-y-6">
        <div>
          <Label htmlFor="invoice_prefix" className="font-medium">
            Invoice number prefix
          </Label>
          <p className="text-sm text-muted-foreground mt-1 mb-3">
            Used as the prefix for auto-generated invoice numbers (e.g. <span className="font-mono">CNB-0001</span>).
            Letters / digits / dashes only, max 8 characters. Leave blank to derive from your band name.
          </p>
          <div className="flex gap-2 max-w-sm">
            <Input
              id="invoice_prefix"
              value={invoicePrefix}
              onChange={(e) => setInvoicePrefix(e.target.value.toUpperCase().slice(0, 8))}
              placeholder="e.g. CNB"
              maxLength={8}
              className="font-mono uppercase"
              data-testid="invoice-prefix-input"
            />
            <Button
              onClick={saveInvoicePrefix}
              disabled={savingInvoicePrefix}
              data-testid="invoice-prefix-save-btn"
            >
              {savingInvoicePrefix ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        <div className="pt-6 border-t border-white/5">
          <Label className="font-medium">Bank details for invoices</Label>
          <p className="text-sm text-muted-foreground mt-1 mb-3">
            These details appear in a "Pay by bank transfer" box on every invoice PDF and in the
            invoice email body. Leave any field blank to omit it.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
            <div className="sm:col-span-2">
              <Label htmlFor="bank_account_name" className="text-xs">Account name</Label>
              <Input
                id="bank_account_name"
                value={bank.bank_account_name}
                onChange={(e) => setBank({ ...bank, bank_account_name: e.target.value })}
                placeholder="e.g. City Brass Band"
                maxLength={80}
                data-testid="bank-account-name-input"
              />
            </div>
            <div>
              <Label htmlFor="bank_sort_code" className="text-xs">Sort code</Label>
              <Input
                id="bank_sort_code"
                value={bank.bank_sort_code}
                onChange={(e) => setBank({ ...bank, bank_sort_code: formatSortCode(e.target.value) })}
                placeholder="12-34-56"
                maxLength={8}
                inputMode="numeric"
                className="font-mono"
                data-testid="bank-sort-code-input"
              />
            </div>
            <div>
              <Label htmlFor="bank_account_number" className="text-xs">Account number</Label>
              <Input
                id="bank_account_number"
                value={bank.bank_account_number}
                onChange={(e) =>
                  setBank({ ...bank, bank_account_number: (e.target.value || "").replace(/\D/g, "").slice(0, 12) })
                }
                placeholder="12345678"
                maxLength={12}
                inputMode="numeric"
                className="font-mono"
                data-testid="bank-account-number-input"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={saveBankDetails}
              disabled={savingBank}
              data-testid="bank-details-save-btn"
            >
              {savingBank ? "Saving..." : "Save bank details"}
            </Button>
          </div>
        </div>

        <div className="border-t border-white/5 pt-4 mt-4">
          <Label className="font-medium">Pay by cheque</Label>
          <p className="text-sm text-muted-foreground mt-1 mb-3">
            If you accept cheques, fill these in and a "Pay by cheque" block will appear on every
            unpaid invoice (PDF + email). Leave blank to hide it.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-2xl">
            <div className="sm:col-span-2">
              <Label htmlFor="cheque_payable_to" className="text-xs">Make cheques payable to</Label>
              <Input
                id="cheque_payable_to"
                value={cheque.cheque_payable_to}
                onChange={(e) => setCheque({ ...cheque, cheque_payable_to: e.target.value })}
                placeholder="e.g. City Brass Band"
                maxLength={120}
                data-testid="cheque-payable-to-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cheque_postal_address" className="text-xs">Postal address (where to send the cheque)</Label>
              <Textarea
                id="cheque_postal_address"
                value={cheque.cheque_postal_address}
                onChange={(e) => setCheque({ ...cheque, cheque_postal_address: e.target.value })}
                placeholder={"The Treasurer\nCity Brass Band\n12 Music Lane\nBristol BS1 2AB"}
                rows={4}
                maxLength={500}
                className="font-mono text-sm"
                data-testid="cheque-postal-address-input"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={saveChequeDetails}
              disabled={savingCheque}
              data-testid="cheque-details-save-btn"
            >
              {savingCheque ? "Saving..." : "Save cheque details"}
            </Button>
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default InvoicingSettingsCard;
